import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, Download, FileText, ChevronDown } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
export function SmartStripsPage() {
  const {
    t
  } = useLanguage();
  const [openAccordion, setOpenAccordion] = useState<string | null>(null);
  const advantages = [t('strips.adv1'), t('strips.adv2'), t('strips.adv3'), t('strips.adv4'), t('strips.adv5'), t('strips.adv6')];
  const docCategories = [{
    id: 'catalogos',
    title: t('prod.docs.catalogs'),
    docs: [{
      name: t('strips.cat1'),
      url: 'https://drive.google.com/uc?export=download&id=19PoNWPk4VsYqRmInjwgSO1sAV4qpgVZj'
    }, {
      name: t('strips.cat2'),
      url: 'https://drive.google.com/uc?export=download&id=1B3zgsHzSHy_og0vWqzkg7TxdLTn34m_Q'
    }, {
      name: t('strips.cat3'),
      url: 'https://drive.google.com/uc?export=download&id=1-fKCMLYFNLuVB8J6pvOrDyXauGoWYuVK'
    }]
  }, {
    id: 'fichas',
    title: t('prod.docs.techSheets'),
    docs: [{
      name: t('strips.tech1'),
      url: 'https://drive.google.com/uc?export=download&id=1zfc3Zv6pLDEPvtLE7n90okV_GjFkHWLA'
    }]
  }];
  return <div className="w-full bg-white pb-32" data-id="element-880">
      <div className="bg-corporate-dark text-white py-24 md:py-40 px-4 relative overflow-hidden" data-id="element-881">
        <div className="absolute inset-0 opacity-20" data-id="element-882">
          <img src="https://cdn.magicpatterns.com/uploads/owj5zLW7ptLceSHFrJ2cQ6/tiras_decorativas.png" alt="Background" className="w-full h-full object-cover" data-id="element-883" />
        </div>
        <div className="max-w-7xl mx-auto relative z-10 mt-10" data-id="element-884">
          <motion.div initial={{
          opacity: 0,
          y: 40
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          ease: [0.25, 0.1, 0.25, 1]
        }} data-id="element-885">
            <span className="text-accent-beige font-medium tracking-[0.2em] uppercase text-sm mb-6 block" data-id="element-886">
              {t('prod.line')}
            </span>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-heading font-bold mb-6 tracking-tight" data-id="element-887">
              SmartStrips
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 max-w-2xl font-light leading-relaxed" data-id="element-888">
              {t('strips.hero.desc')}
            </p>
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-20 lg:mt-28" data-id="element-889">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-20" data-id="element-890">
          <motion.div initial={{
          opacity: 0,
          x: -40
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: 0.2,
          duration: 0.8,
          ease: [0.25, 0.1, 0.25, 1]
        }} data-id="element-891">
            <h2 className="text-4xl font-heading font-bold text-corporate-dark mb-8 tracking-tight" data-id="element-892">
              {t('strips.sec.title')}
            </h2>
            <div className="text-lg text-gray-600 font-light leading-relaxed space-y-6 mb-12" data-id="element-893">
              <p data-id="element-894">{t('strips.sec.p1')}</p>
              <p data-id="element-895">{t('strips.sec.p2')}</p>
            </div>

            <h3 className="text-2xl font-heading font-bold text-corporate-dark mb-8 tracking-tight" data-id="element-896">
              {t('prod.adv.title')}
            </h3>
            <ul className="space-y-5 mb-12" data-id="element-897">
              {advantages.map((adv, idx) => <li key={idx} className="flex items-start" data-id="element-898">
                  <CheckCircle2 className="h-6 w-6 text-corporate-blue mr-4 flex-shrink-0 mt-0.5" data-id="element-899" />
                  <span className="text-gray-700 text-lg font-light" data-id="element-900">
                    {adv}
                  </span>
                </li>)}
            </ul>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 40
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: 0.3,
          duration: 0.8,
          ease: [0.25, 0.1, 0.25, 1]
        }} className="space-y-8" data-id="element-901">
            <div className="h-96 bg-gray-100 rounded-sm overflow-hidden group" data-id="element-902">
              <img src="https://cdn.magicpatterns.com/uploads/owj5zLW7ptLceSHFrJ2cQ6/tiras_decorativas.png" alt="SmartStrips - Tiras Decorativas" className="w-full h-full object-cover transition-transform duration-700 ease-premium group-hover:scale-105" data-id="element-903" />
            </div>
            <div className="grid grid-cols-2 gap-8" data-id="element-904">
              <div className="bg-neutral-grey p-8 rounded-sm" data-id="element-905">
                <h4 className="text-xl font-heading font-bold text-corporate-dark mb-3 tracking-tight" data-id="element-906">
                  {t('strips.app1.title')}
                </h4>
                <p className="text-base text-gray-600 font-light leading-relaxed" data-id="element-907">
                  {t('strips.app1.desc')}
                </p>
              </div>
              <div className="bg-neutral-grey p-8 rounded-sm" data-id="element-908">
                <h4 className="text-xl font-heading font-bold text-corporate-dark mb-3 tracking-tight" data-id="element-909">
                  {t('strips.app2.title')}
                </h4>
                <p className="text-base text-gray-600 font-light leading-relaxed" data-id="element-910">
                  {t('strips.app2.desc')}
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Documentación SmartStrips */}
        <motion.div id="documentacion-smartstrips" initial={{
        opacity: 0,
        y: 40
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.4,
        duration: 0.8,
        ease: [0.25, 0.1, 0.25, 1]
      }} className="mt-24 lg:mt-32 scroll-mt-24" data-id="element-911">
          <h3 className="text-3xl font-heading font-bold text-corporate-dark mb-3 tracking-tight" data-id="element-912">
            {t('prod.docs.strips.title')}
          </h3>
          <p className="text-lg text-gray-500 font-light mb-12 max-w-3xl" data-id="element-913">
            {t('prod.docs.strips.desc')}
          </p>

          <div className="space-y-4" data-id="element-914">
            {docCategories.map(category => <div key={category.id} className="bg-white border border-gray-100 rounded-sm shadow-md overflow-hidden" data-id="element-915">
                <button onClick={() => setOpenAccordion(openAccordion === category.id ? null : category.id)} className="w-full flex items-center justify-between px-8 py-6 text-left hover:bg-neutral-grey transition-colors duration-300" data-id="element-916">
                  <div data-id="element-917">
                    <h4 className="text-xl font-heading font-bold text-corporate-dark tracking-tight" data-id="element-918">
                      {category.title}
                    </h4>
                    <p className="text-sm text-gray-500 font-light mt-1" data-id="element-919">
                      {category.docs.length}{' '}
                      {category.docs.length === 1 ? t('prod.docs.count.one') : t('prod.docs.count.many')}
                    </p>
                  </div>
                  <ChevronDown className={`h-5 w-5 text-corporate-blue flex-shrink-0 ml-4 transition-transform duration-300 ${openAccordion === category.id ? 'rotate-180' : ''}`} data-id="element-920" />
                </button>
                <AnimatePresence initial={false} data-id="element-921">
                  {openAccordion === category.id && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.35,
                ease: [0.25, 0.1, 0.25, 1]
              }} className="overflow-hidden" data-id="element-922">
                      <div className="divide-y divide-gray-100 border-t border-gray-100" data-id="element-923">
                        {category.docs.map((doc, idx) => <div key={idx} className="flex flex-col sm:flex-row sm:items-center justify-between px-8 py-5 hover:bg-neutral-grey transition-colors duration-300" data-id="element-924">
                            <div className="flex items-center mb-4 sm:mb-0" data-id="element-925">
                              <FileText className="h-5 w-5 text-corporate-blue mr-4 flex-shrink-0" data-id="element-926" />
                              <span className="text-base text-gray-800 font-medium" data-id="element-927">
                                {doc.name}
                              </span>
                            </div>
                            <a href={doc.url} target="_blank" rel="noopener noreferrer" download className="inline-flex items-center justify-center px-5 py-2.5 border border-corporate-blue text-corporate-blue hover:bg-corporate-blue hover:text-white transition-all duration-500 ease-premium rounded-md text-sm font-medium w-full sm:w-auto hover:scale-[1.02]" data-id="element-928">
                              <Download className="mr-2 h-4 w-4" data-id="element-929" />
                              {t('prod.docs.download')}
                            </a>
                          </div>)}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>)}
          </div>
        </motion.div>
      </div>
    </div>;
}